import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'

// Área com rolagem horizontal (tabelas largas, grade de planos) com uma segunda
// barra de rolagem acima do conteúdo, sincronizada com a de baixo. A barra de
// cima só aparece quando há o que rolar — no celular as tabelas viram cards e
// ela some sozinha.
export function HorizontalScroll({ className, topClassName = '', label, children }: { className: string; topClassName?: string; label?: string; children: ReactNode }) {
  const bodyRef = useRef<HTMLDivElement>(null)
  const topRef = useRef<HTMLDivElement>(null)
  const [track, setTrack] = useState(0)

  useLayoutEffect(() => {
    const body = bodyRef.current
    if (!body) return
    const update = () => {
      const overflow = body.scrollWidth - body.clientWidth
      // Mesma distância de rolagem nas duas barras, mesmo que a de baixo divida
      // espaço com uma barra vertical (scrollbar-gutter).
      setTrack(overflow > 1 ? overflow + (topRef.current?.clientWidth || body.clientWidth) : 0)
      if (topRef.current) topRef.current.scrollLeft = body.scrollLeft
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(body)
    // A barra de cima muda de largura ao aparecer; a distância é recalculada.
    if (topRef.current) observer.observe(topRef.current)
    const watchContent = () => { Array.from(body.children).forEach(child => observer.observe(child)) }
    watchContent()
    // Linhas novas (paginação, filtros) podem trocar o conteúdo sem mudar o contêiner.
    const mutations = new MutationObserver(() => { watchContent(); update() })
    mutations.observe(body, { childList: true })
    return () => { observer.disconnect(); mutations.disconnect() }
  }, [])

  const sync = (from: HTMLDivElement, to: HTMLDivElement | null) => {
    if (to && to.scrollLeft !== from.scrollLeft) to.scrollLeft = from.scrollLeft
  }

  return <>
    <div ref={topRef} className={`h-scroll-top${topClassName ? ` ${topClassName}` : ''}`} hidden={!track} aria-hidden="true" onScroll={event => sync(event.currentTarget, bodyRef.current)}>
      <div style={{ width: track }}/>
    </div>
    <div ref={bodyRef} className={className} {...(label ? { tabIndex: 0, role: 'region', 'aria-label': label } : {})} onScroll={event => sync(event.currentTarget, topRef.current)}>{children}</div>
  </>
}
